import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import { Button, Field, Modal, Stack, TextInput, Text, tokens } from '@hotjar/hotsauce';
import * as URL from '@hotjar/insights-common/lib/url';

interface ManualRetakerUrlModalProps {
  isOpen: boolean;
  onSubmit: (url: string) => void;
  onClose: () => void;
}

const StyledField = styled(Field)`
  width: unset;
`;

const Error = styled(Text)`
  display: inline-block;
  margin-top: ${tokens.spacer.x0};
`;

const formId = 'url-form';
const urlInputId = 'manual-retaker-url-input';

export const ManualRetakerUrlModal = ({
  isOpen,
  onSubmit,
  onClose,
}: ManualRetakerUrlModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { register, handleSubmit, errors } = useForm<{ url: string }>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  return (
    <Modal
      appElementSelector="#root"
      contentLabel={t('heatmaps:manual-retaker.url-modal.content-label')}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Header
        title={t('heatmaps:manual-retaker.url-modal.title')}
        subtitle={t('heatmaps:manual-retaker.url-modal.subtitle')}
      />
      <Modal.Body>
        <form onSubmit={handleSubmit(({ url }) => onSubmit(url))} id={formId}>
          <Stack direction="column" preset="form">
            <StyledField
              labelText={t('heatmaps:manual-retaker.url-modal.enter-url')}
              inputId={urlInputId}
            >
              <TextInput
                name="url"
                placeholder={t('heatmaps:manual-retaker.url-modal.placeholder')}
                ref={register({
                  required: t<string>('heatmaps:manual-retaker.url-modal.required-url'),
                  validate: (value: string) =>
                    URL.validateUrl(value, true) ||
                    t<string>('heatmaps:manual-retaker.url-modal.invalid-url'),
                })}
                isInvalid={!!errors.url}
              />
              {errors.url && (
                <Error as="span" textColor="text_danger" textType="caption">
                  {errors.url?.message}
                </Error>
              )}
            </StyledField>
          </Stack>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={onClose}>
          {t('heatmaps:manual-retaker.url-modal.cancel')}
        </Button>
        <Button kind="primary" type="submit" form={formId}>
          {t('heatmaps:manual-retaker.url-modal.continue')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
